import React, { useState } from "react";
import { styled } from "@mui/material";
import buildUrl from "build-url-ts";
import { SolutionDiagnosticsOption } from "../../views/GeneralTask/generalTask.types";
import { REPORT_URL } from "../../utils/environmentVariables";

export const PLACEHOLDER_IMAGE = "/img-placeholder.jpg";

/**
 * The resource directory a report figure is served from, which differs between
 * the two kinds of report. Full URL is
 * `{REPORT_URL}/{id}/{resourceDir}/{finalPath}`.
 */
export const SOLUTION_REPORT_DIR = "solution_report/resources";
export const RUPTURE_SET_REPORT_DIR = "DiagnosticsReport/resources";

export const reportUrl = (resourceDir: string, id: string, path: string): string => {
  return (
    buildUrl(REPORT_URL, {
      path: `/${id}/${resourceDir}/${path}`,
    }) || ""
  );
};

const StyledImage = styled("img")(() => ({
  padding: "5px",
  maxHeight: "100%",
  width: "100%",
  objectFit: "contain",
}));

interface ReportImageProps {
  id: string;
  option: SolutionDiagnosticsOption;
  resourceDir: string;
  style?: React.CSSProperties;
}

/**
 * Renders a report figure, working through `option.fallbackPaths` on each load
 * failure before settling on the placeholder. Older tasks keep older filenames
 * for the same figure, so the first candidate is not always the one that exists.
 *
 * Callers must give this a `key` that changes with `id`/`option`, so that paging
 * to another solution restarts the candidate list from the top.
 */
const ReportImage: React.FC<ReportImageProps> = ({ id, option, resourceDir, style }: ReportImageProps) => {
  const candidates = [option.finalPath, ...(option.fallbackPaths ?? [])];
  const [index, setIndex] = useState<number>(0);
  const exhausted = index >= candidates.length;

  return (
    <StyledImage
      style={style}
      src={exhausted ? PLACEHOLDER_IMAGE : reportUrl(resourceDir, id, candidates[index])}
      alt={option.displayName}
      onError={() => {
        // Never advance past the placeholder: if that 404s too there is nothing
        // left to try, and re-setting `src` would reload it forever.
        setIndex((current) => (current < candidates.length ? current + 1 : current));
      }}
    />
  );
};

export default ReportImage;
